import React, { useEffect } from "react";
import ThemeProvider from "@material-ui/styles/ThemeProvider";
import { NotificationProvider } from "./contexts/NotificationContext";
import { ErrorProvider } from './contexts/ErrorContext';
import { RoleProvider } from "./contexts/RolesContext";
import { Base } from "./layouts"; /**@todo rename to something like "BaseUILayout" or "BaseLayout". "Base" is a bit too vague... */
import { BrowserRouter as Router, Switch, Route, Redirect } from "react-router-dom";
import { Provider, ErrorBoundary } from '@rollbar/react';
import {
  Loans,
  Loan,
  DailyProcessing,
  UserAdmin,
  Dealers,
  Dealer,
  TestTransactions,
  Tools,
  Login,
  ForgotPassword,
  ResetPassword,
  PageNotFound,
  Affiliates,
} from "./pages";
import theme from "./utils/theme";
import "./App.css";
import { createBrowserHistory } from "history";
import { readCookie } from "./utils/cookiesHandler";
import { PUBLIC_PATHS } from "./utils/constants";

function App() {
  const history = createBrowserHistory();
  const publicPages = PUBLIC_PATHS;
  // Read `userData` cookie (JSON: { name, emailAddress, id }).
  let userData = {};
  try {
    userData = JSON.parse(readCookie("userData")) || {};
  } catch {
    // A malformed cookie is non-fatal; userData stays {}.
    userData = {};
  }

  const rollbarConfig = {
    accessToken: process.env.REACT_APP_ROLLBAR_ACCESS_TOKEN,
    captureUncaught: true,
    captureUnhandledRejections: true,
    // 401s are expected auth failures — the response interceptor already
    // redirects to /login — so don't report them to Rollbar as errors.
    ignoredMessages: ['Request failed with status code 401'],
    // A page saved to disk (Ctrl+S) and reopened over file:// still boots the
    // bundle, but location.pathname is then the local file path, which matches
    // no route — so the catch-all <Redirect to="/404" /> calls history.replace
    // and throws SecurityError, since history rewrites are forbidden when the
    // file: path differs. Nothing a saved page reports is actionable, so drop
    // the whole class rather than matching one browser's message text.
    checkIgnore: () => window.location.protocol === 'file:',
    payload: {
      environment: process.env.REACT_APP_DEPLOY_ENV === 'production' ? 'PROD' : process.env.REACT_APP_DEPLOY_ENV,
      // Attach the signed-in user so errors are attributable in Rollbar.
      ...(userData.id && {
        person: {
          id: String(userData.id),
          username: userData.name,
          email: userData.emailAddress,
        },
      }),
    },
  };

  const allRoles = ["admin", "daily_processing_write", "daily_processing_read", "ach_admin", "user_admin", "dealer_browser", "super_admin"]
  const roles = process.env.REACT_APP_DEPLOY_ENV.includes("LOCAL") ? allRoles : JSON.parse(readCookie("roles"))

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (process.env.REACT_APP_DEPLOY_ENV.includes("LOCAL")) return;

      // if the page is not public, and the user isn't logged in, redirect to login
      if (!readCookie("roles") && !([...publicPages, "/404"].includes(location.pathname))){
        history.push("/login")
      }

      // if the page is public, but the user is logged in, redirect to "/"
      if (readCookie("roles") && publicPages.includes(location.pathname)){
        history.push("/lms")
      }
    })

    return () => {
      unlisten();
    }
  }, [history, publicPages])

  return (
    <Provider config={rollbarConfig}>
    <ErrorBoundary>
    <ThemeProvider theme={theme}>
      <NotificationProvider>
      <ErrorProvider>
        <RoleProvider roles={roles}>
          <Router history={history}>
            <Switch>
              <Route exact path="/login" render={({history}) => <Login history={history} />} />
              <Route exact path="/" render={({history}) => <Login history={history} />} />
              <Route exact path="/forgot-password" render={({history}) => <ForgotPassword history={history} />} />
              <Route exact path="/reset-password" render={({history}) => <ResetPassword history={history} />} />
              <Route exact path="/404" render={(props) => <PageNotFound {...props} />} />
              <Route
                render={({history}) => (
                  <Base>
                    <Switch>
                      <Route exact path="/lms" render={(props) => <Loans />} />
                      <Route
                        exact
                        path="/lms/loans" // maybe this path is unnecesary, considering the one above...
                        render={(props) => <Loans />}
                       />
                      <Route exact path="/lms/daily-processing" render={(props) => <DailyProcessing />} />
                      <Route path="/lms/loans/:loanNumber" render={(props) => <Loan />} />
                      <Route exact path="/admin" render={(props) => <Tools />} />
                      <Route exact path="/admin/users" render={(props) => <UserAdmin />} />
                      <Route path="/admin/Tools" render={(props) => <Tools />} />
                      <Route exact path="/dealers" render={(props) => <Dealers />} />
                      <Route exact path="/dealers/test-transactions" render={(props) => <TestTransactions />} />
                      <Route path="/dealers/:dealerNumber" render={(props) => <Dealer />} />
                      <Route exact path="/affiliates" render={(props) => <Affiliates />} />
                      <Route><Redirect to="/404" /></Route>
                    </Switch>
                  </Base>
                )}
              />
            </Switch>
          </Router>
        </RoleProvider>
      </ErrorProvider>
      </NotificationProvider>
    </ThemeProvider>
    </ErrorBoundary>
    </Provider>
  );
}

export default App;
